import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  ChevronLeft, ChevronRight, ChevronDown, LogOut, User, Menu, Mail, IndianRupee,
  Globe, HardDrive, ShieldCheck, CreditCard, FileText,
  Shield, Building2, Settings,
} from 'lucide-react';
import {
  getAllLmsNavItems,
  userIsTenantAdmin,
  filterNavByModuleAccess,
} from '@/lib/lmsNavRegistry';
import ColorThemeMenu from './ColorThemeMenu';

function getUserInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function getDisplayRole(user) {
  if (!user) return 'User';
  if (user.is_system_admin) return 'System Admin';
  if (user.is_tenant_admin) return 'Admin';
  const roles = user.roles || [];
  if (roles.length > 0) {
    return roles[0].replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return 'User';
}

/** Build the navigation array for a system-admin user. */
function buildSystemAdminNav() {
  return [
    { name: 'Platform Dashboard', href: '/system/tenants', icon: Globe },
    { name: 'Platform Settings', href: '/system/credit-config', icon: CreditCard },
    { name: 'Credit Management', href: '/system/credit-management', icon: IndianRupee },
    { name: 'Email Config', href: '/system/email-config', icon: Mail },
    { name: 'Email Templates', href: '/system/email-templates', icon: FileText },
    { name: 'Email Logs', href: '/system/email-logs', icon: FileText },
    { name: 'Storage (S3)', href: '/system/storage', icon: HardDrive },
    { name: 'CAPTCHA', href: '/system/captcha', icon: ShieldCheck },
    { name: 'Agreement Templates', href: '/system/agreement-templates', icon: FileText },
  ];
}

/** Append risk_manager backward-compat items to a tenant nav if not already present. */
function appendRiskManagerItems(navigation, user) {
  const result = [...navigation];
  if (!(user?.module_access || []).includes('system')) {
    const riskItem = getAllLmsNavItems().find((i) => i.href === '/risk');
    if (riskItem && !result.some((i) => i.href === '/risk')) result.push(riskItem);
  }
  if (!(user?.module_access || []).includes('loan_operations')) {
    const freezeItem = getAllLmsNavItems().find((i) => i.href === '/loan-freeze');
    if (freezeItem && !result.some((i) => i.href === '/loan-freeze')) result.push(freezeItem);
  }
  return result;
}

/** Build the navigation array for a regular tenant user. */
function buildTenantNav(user) {
  let nav = filterNavByModuleAccess(getAllLmsNavItems(), user);
  // Backward-compat: Risk page and Loan Freeze also allowed for legacy risk_manager role
  if (!userIsTenantAdmin(user) && (user?.roles || []).includes('risk_manager')) {
    nav = appendRiskManagerItems(nav, user);
  }
  return nav;
}

export default function AppShell({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({ 'User Management': true });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, tenant, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Build navigation from LMS sidebar registry, filtered by module visibility
  const navigation = user?.is_system_admin
    ? buildSystemAdminNav()
    : buildTenantNav(user);

  const companyName = tenant?.company_name || (user?.is_system_admin ? 'LMS Admin' : 'LMS');
  const logoInitial = companyName.charAt(0).toUpperCase();
  const displayName = user?.full_name || 'User';
  const displayRole = getDisplayRole(user);
  const userInitials = getUserInitials(displayName);

  const flatLinks = navigation.flatMap((item) => {
    if (item.type === 'group') return item.children || [];
    if (item.href) return [item];
    return [];
  });

  const linkIsActive = (item) => {
    const hasMoreSpecificMatch = flatLinks.some(
      (other) =>
        other.href &&
        other.href !== item.href &&
        other.href.startsWith(item.href) &&
        location.pathname.startsWith(other.href),
    );
    if (hasMoreSpecificMatch) return false;
    return (
      location.pathname === item.href ||
      (item.href !== '/dashboard' && location.pathname.startsWith(`${item.href}/`))
    );
  };

  const toggleGroup = (name, event) => {
    const button = event?.currentTarget;
    const viewport = button?.closest('[data-radix-scroll-area-viewport]');
    const anchorTop = button?.getBoundingClientRect().top ?? null;

    setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));

    // Keep the group header visually fixed; only children expand/collapse below it.
    if (!viewport || anchorTop == null) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const delta = button.getBoundingClientRect().top - anchorTop;
        if (delta !== 0) viewport.scrollTop += delta;
      });
    });
  };

  const renderNavItems = ({ showLabels, onNavigate }) =>
    navigation.map((item) => {
      if (item.type === 'separator') {
        return showLabels ? (
          <div key={`sep-${item.label}`} className="pt-4 pb-2 px-3">
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: 'hsl(var(--sidebar-fg, 214 32% 91%) / 0.5)' }}
            >
              {item.label}
            </span>
          </div>
        ) : (
          <div key={`sep-${item.label}`} className="py-2">
            <div className="border-t border-white/10" />
          </div>
        );
      }

      if (item.type === 'group') {
        const childActive = (item.children || []).some((c) => linkIsActive(c));
        // Toggle is click-driven only; childActive still styles the parent when a child route is active.
        const isOpen = !!openGroups[item.name];
        return (
          <div key={item.name} className="space-y-0.5">
            <button
              type="button"
              aria-expanded={isOpen}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => toggleGroup(item.name, e)}
              data-testid={`nav-group-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/5"
              style={{
                color: childActive
                  ? 'hsl(var(--sidebar-fg, 214 32% 91%))'
                  : 'hsl(var(--sidebar-fg, 214 32% 91%) / 0.7)',
              }}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {showLabels && (
                <>
                  <span className="flex-1 text-left">{item.name}</span>
                  <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>
            {showLabels && (
              <div
                className={`ml-3 space-y-0.5 border-l border-white/10 pl-2 ${isOpen ? '' : 'hidden'}`}
                aria-hidden={!isOpen}
              >
                {(item.children || []).map((child) => {
                  const isActive = linkIsActive(child);
                  return (
                    <Link
                      key={child.name}
                      to={child.href}
                      tabIndex={isOpen ? undefined : -1}
                      onClick={onNavigate}
                      data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive ? 'text-white' : 'hover:bg-white/5'
                      }`}
                      style={{
                        color: isActive
                          ? 'hsl(var(--sidebar-fg, 214 32% 91%))'
                          : 'hsl(var(--sidebar-fg, 214 32% 91%) / 0.7)',
                        backgroundColor: isActive
                          ? 'hsl(var(--sidebar-active, 221 83% 40%) / 0.3)'
                          : undefined,
                      }}
                    >
                      <child.icon className="h-4 w-4 flex-shrink-0" />
                      <span>{child.name}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      const isActive = linkIsActive(item);
      return (
        <Link
          key={item.name}
          to={item.href}
          onClick={onNavigate}
          data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
          className={`flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            isActive ? 'text-white' : 'hover:bg-white/5'
          }`}
          style={{
            color: isActive
              ? 'hsl(var(--sidebar-fg, 214 32% 91%))'
              : 'hsl(var(--sidebar-fg, 214 32% 91%) / 0.7)',
            backgroundColor: isActive
              ? 'hsl(var(--sidebar-active, 221 83% 40%) / 0.3)'
              : undefined,
          }}
        >
          <item.icon className="h-5 w-5 flex-shrink-0" />
          {showLabels && <span>{item.name}</span>}
        </Link>
      );
    });

  return (
    <div className="app-container" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border sidebar ${
          collapsed ? 'sidebar-collapsed' : 'sidebar-expanded'
        } hidden lg:flex`}
        style={{ backgroundColor: 'hsl(var(--sidebar-bg, 222 47% 6%))' }}
        data-testid="sidebar-nav"
      >
        {/* Logo / Company Name */}
        <div className="flex h-14 items-center px-4 border-b border-white/10 gap-3">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt="" className="h-8 w-8 rounded object-contain" />
          ) : (
            <div className="h-8 w-8 rounded bg-white/10 flex items-center justify-center text-sm font-bold" style={{ color: 'hsl(var(--sidebar-fg, 214 32% 91%))' }}>
              {logoInitial}
            </div>
          )}
          {!collapsed && (
            <span className="font-semibold text-sm truncate" style={{ color: 'hsl(var(--sidebar-fg, 214 32% 91%))' }}>
              {companyName}
            </span>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-2 space-y-1">
            {renderNavItems({ showLabels: !collapsed })}
          </nav>
        </ScrollArea>

        {/* Collapse toggle */}
        <div className="p-2 border-t border-white/10">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-white/60 hover:text-white hover:bg-white/10"
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-collapse-button"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex-1 flex flex-col min-h-0 min-w-0 ${collapsed ? 'lg:ml-[60px]' : 'lg:ml-[240px]'} transition-[margin] duration-200`}>
        {/* Top bar */}
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4 lg:px-6" data-testid="topbar">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex-1 max-w-md mx-4 hidden md:block">
          </div>

          <div className="flex items-center gap-2">
            {user?.is_system_admin && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-medium" data-testid="system-admin-badge">
                System Admin
              </span>
            )}
            {(user?.is_tenant_admin || user?.is_system_admin) && <ColorThemeMenu />}
            <Separator orientation="vertical" className="h-6 mx-2" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 h-auto py-1.5 px-2" data-testid="user-menu-trigger">
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-xs font-semibold text-primary-foreground">
                    {userInitials}
                  </div>
                  <div className="hidden md:flex flex-col items-start text-left leading-tight">
                    <span className="text-sm font-semibold">{displayName}</span>
                    <span className="text-xs text-muted-foreground">{displayRole}</span>
                  </div>
                  <ChevronDown className="hidden md:block h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold">{displayName}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <div className="px-3 py-2 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>Role: {displayRole}</span>
                  </div>
                  {tenant?.company_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="truncate">Tenant: {tenant.company_name}</span>
                    </div>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                  <User className="h-4 w-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                {user?.is_tenant_admin && (
                  <DropdownMenuItem onClick={() => navigate('/tenant/settings')} data-testid="settings-link">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive" data-testid="logout-button">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-auto min-h-0 min-w-0">
          <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6 max-w-full min-w-0">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile sidebar overlay + drawer */}
      {mobileOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/50 lg:hidden border-0 p-0 cursor-default"
            onClick={() => setMobileOpen(false)}
            aria-label="Close sidebar"
          />
          <aside
            className="fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border w-[260px] lg:hidden"
            style={{ backgroundColor: 'hsl(var(--sidebar-bg, 222 47% 6%))' }}
          >
            {/* Logo / Company Name */}
            <div className="flex h-14 items-center px-4 border-b border-white/10 gap-3">
              {tenant?.logo_url ? (
                <img src={tenant.logo_url} alt="" className="h-8 w-8 rounded object-contain" />
              ) : (
                <div className="h-8 w-8 rounded bg-white/10 flex items-center justify-center text-sm font-bold" style={{ color: 'hsl(var(--sidebar-fg, 214 32% 91%))' }}>
                  {logoInitial}
                </div>
              )}
              <span className="font-semibold text-sm truncate" style={{ color: 'hsl(var(--sidebar-fg, 214 32% 91%))' }}>
                {companyName}
              </span>
            </div>

            {/* Navigation */}
            <ScrollArea className="flex-1 py-4">
              <nav className="px-2 space-y-1">
                {renderNavItems({ showLabels: true, onNavigate: () => setMobileOpen(false) })}
              </nav>
            </ScrollArea>
          </aside>
        </>
      )}
    </div>
  );
}

AppShell.propTypes = {
  children: PropTypes.node.isRequired,
};
